<template>
    <div v-show="isOpen" :aria-hidden="!isOpen">
        <DsModal
            class="unlayer-email-broadcast-wizard-modal"
            is-open
            size="fullscreen"
            sticky-desktop-header
            :show-back="currentStageIndex > $options.STAGE_INDEXES.TEMPLATE"
            :show-close="currentStageIndex === $options.STAGE_INDEXES.TEMPLATE"
            :intercept-close="currentStageIndex > $options.STAGE_INDEXES.TEMPLATE"
            :title="modalTitle"
            @back="handleModalBack"
            @close="$emit('close')"
        >
            <template #title>
                <div class="header-left">
                    <DsModalHeaderTitle class="header-title" :title="modalTitle" />

                    <DsIconButton
                        name="help-circle"
                        data-qa="learn-more-button"
                        as="a"
                        :href="$options.HELP_URL"
                        target="_blank"
                    />
                </div>
            </template>

            <template #header>
                <portal-target name="unlayer-email-editor-actions" />
            </template>

            <template #default>
                <div class="stage-indicator-container">
                    <DsGuide
                        class="template-change-tip-popover"
                        data-qa="unlayer-email-broadcast-template-select-guide"
                        position="bottom"
                        :body-text="$t('email.broadcast.unlayerWizard.templateSelectGuideBody')"
                        :confirm-text="$t('global.okgotit')"
                        :show="isTemplateChangeTipVisible"
                        @confirm="handleTemplateChangeTipClosed"
                    />
                    <StageIndicator
                        :active-stage="currentStageIndex"
                        :stages="stages"
                        @go-to-stage="goToStage"
                    />
                </div>

                <section class="stages-container">
                    <div v-if="isInitializing" class="spinner-container">
                        <DsSpinner data-qa="loading-spinner" />
                    </div>

                    <TemplateSelectionStage
                        v-else-if="currentStageIndex === $options.STAGE_INDEXES.TEMPLATE"
                        preview-enabled
                        :is-max-classic="isMaxClassic"
                        :marketing-email-id="email.source === $options.SOURCES.CAMPAIGN ? email.id : null"
                        :template-id="email.templateId"
                        :unlayer-email-logo-tool-enabled="coreConfig.unlayerEmailLogoToolEnabled"
                        :emails-thryv-theme-enabled="coreConfig.emailsThryvThemeEnabled"
                        :thryv-interim-unified-theme-enabled="coreConfig.thryvInterimUnifiedThemeEnabled"
                        :source="email.source"
                        :template-html-exists="hasContent"
                        :external-template-id="externalTemplateId"
                        @template-created="handleTemplateCreated"
                        @template-selected="handleTemplateSelected"
                        @create-saved-template="handleCreateSavedTemplate"
                        @close="$emit('close')"
                    />

                    <UnlayerEmailEditStage
                        v-else-if="currentStageIndex === $options.STAGE_INDEXES.CREATE"
                        :email-template="emailTemplate"
                        :external-template-id="externalTemplateId"
                        :broadcast-id="email.source === $options.SOURCES.BROADCAST? email.id : null"
                        :app-id="coreConfig.appId"
                        :funnel-id="funnelId"
                        :environment="coreConfig.environment"
                        :unlayer-project-id="coreConfig.unlayerProjectId"
                        :merge-fields="mergeFields"
                        :users="users"
                        :user-signatures="userSignatures"
                        :campaign-links="campaignLinks"
                        :booking-link-hostname="coreConfig.bookingLinkHostname"
                        :appointment-types="appointmentTypes"
                        :tags="tags"
                        :brand-colors="brandColors"
                        :unlayer-appointment-tool-enabled="unlayerAppointmentToolEnabled"
                        :unlayer-ai-tools-enabled="coreConfig.unlayerAiToolsEnabled"
                        :unlayer-smart-merge-tags-enabled="coreConfig.unlayerSmartMergeTagsEnabled"
                        :unlayer-builder-version-override="coreConfig.unlayerBuilderVersionOverride"
                        :show-verified-domains-new-keys-only-enabled="coreConfig.showVerifiedDomainsNewKeysOnlyEnabled"
                        :use-unlayer-builder-version-override-enabled="coreConfig.useUnlayerBuilderVersionOverrideEnabled"
                        :unlayer-email-logo-tool-enabled="coreConfig.unlayerEmailLogoToolEnabled"
                        :is-max-classic="isMaxClassic"
                        :content-risk-messages="contentRiskMessages"
                        :content-risk-quality="contentRiskQuality"
                        :fetch-content-risks="fetchContentRisks"
                        :content-risk-sendable="contentRiskSendable"
                        :loading-content-risk="loadingContentRisk"
                        :source="email.source"
                        :saved-template-to-create="savedTemplateToCreate"
                        :unlayer-extra-tags-override-enabled="coreConfig.isUnlayerExtraTagOverrideEnabled"
                        :is-wizard-open="isOpen"
                        @update-email-template="$emit('update-template', $event)"
                        @saved-template-created="handleSavedTemplateCreated"
                        @unsaved-changes="$emit('unsaved-changes', $event)"
                        @template-edit-complete="$emit('close')"
                        @save="$emit('save', $event)"
                        @close="$emit('close')"
                    />
                </section>
            </template>
        </DsModal>

        <portal-target name="unlayer-email-editor-root" />
    </div>
</template>

<script>
import StageIndicator from '../StageIndicator';
import TemplateSelectionStage from '@/components/unlayer/TemplateSelectionStage';
import UnlayerEmailEditStage from '@/components/unlayer/UnlayerEmailEditStage';
import { STAGE_STATUS, UNLAYER_EXTRA_TAGS_PAGE_SIZE, UNLAYER_EXTRA_TAGS_PAGE_COUNT } from '../constants/emailBroadcast.constants';
import { STEPS } from '@/components/unlayer/constants/editor.constants';
import { TUTORIAL_TYPES } from '../constants/tutorials.constants';
import {
    SOURCES,
    UNLAYER_EMAIL_BROADCAST_WIZARD_STAGE_INDEXES as STAGE_INDEXES,
    UNLAYER_BUILDER_CUSTOMIZATION_HELP_URL as HELP_URL,
} from '@/components/unlayer/constants/unlayer.constants';
import {
    getMergeFields,
    getCampaignLinks,
    getUsers,
    getUserSignatures,
    getAppointmentTypes,
    getTags,
    getBrandPalette,
} from '@/api/campaigns/campaignUnlayerEmail';
import { updateFtuItem, loadFtu } from '@/ftu.utils';
import {
    mergeCategoryAndTagName,
    validateUnlayerEmail
} from '@/components/unlayer/unlayerEmail.util';

import sentry from '@/analytics/sentry';


const { TEMPLATE_CHANGE_GUIDE_DISPLAY_DELAY_MS, TEMPLATE_CHANGE_GUIDE } = TUTORIAL_TYPES.UNLAYER_BROADCAST;

export default {
    SOURCES,
    STAGE_INDEXES,
    HELP_URL,

    name: 'UnlayerEmailWizardModal',

    components: {
        StageIndicator,
        TemplateSelectionStage,
        UnlayerEmailEditStage,
    },

    props: {
        email: {
            type: Object,
            default: () => ({}),
        },
        funnelId: [String, Number],
        externalTemplateId: String,
        marketingEmailId: String,
        trackedLinks: {
            type: Array,
            default: () => [],
        },
        isMaxClassic: Boolean,
        coreConfig: Object,
        contentRiskMessages: {
            type: Array,
            default: () => [],
        },
        contentRiskQuality: String,
        contentRiskSendable: Boolean,
        loadingContentRisk: Boolean,
        fetchContentRisks: {
            type: Function,
            default: (x) => x,
        },
        isOpen: Boolean,
        initialStage: Number,
    },

    data() {
        return {
            isTemplateChangeTipVisible: false,
            currentStageIndex: this.initialStage ?? (
validateUnlayerEmail(this.email, STEPS.CONTENT)
                    ? STAGE_INDEXES.CREATE
                    : STAGE_INDEXES.TEMPLATE
            ),
            tags: [],
            brandColors: [],
            appointmentTypes: [],
            mergeFields: [],
            users: [],
            campaignLinks: [],
            userSignatures: {},
            unlayerAppointmentToolEnabled: false,
            isInitializing: true,
            ftuItems: null,
            ftuItemsLoaded: false,
            timeoutHandle: null,
            savedTemplateToCreate: null,
        };
    },

    async created() {
        await Promise.allSettled([
            this.fetchMergeFields(),
            this.fetchUsers(),
            this.fetchUserSignatures(),
            this.fetchAppointmentTypes(),
            this.fetchTags(),
            this.fetchUnlayerOptions(),
            this.loadFtuItems(),
            this.fetchCampaignLinks(),
            this.fetchBrandColors()
        ]);

        this.isInitializing = false;
    },

    watch: {
        currentStageIndex() {
            this.templateChangeGuideRoutine();
        },

        ftuItemsLoaded(newVal) {
            if (newVal) {
                this.templateChangeGuideRoutine();
            }
        },

        isOpen(newVal) {
            if (newVal) {
                this.currentStageIndex = this.initialStage ?? (
                    this.hasContent ? STAGE_INDEXES.CREATE : STAGE_INDEXES.TEMPLATE
                );
            }
        },
    },

    computed: {
        modalTitle() {
            if (this.email.source === SOURCES.BROADCAST) {
                return this.$t('email.broadcast.unlayerWizard.title');
            }

            return this.$t('funnel.unlayerEmail.unifiedFlow.modal.title');
        },

        stages() {
            const { IN_PROGRESS, COMPLETE } = STAGE_STATUS;

            return [
                this.$t('email.broadcast.unlayerWizard.templateStep'),
                this.$t('email.broadcast.unlayerWizard.createStep'),
            ].map((label, index) => {
                const isComplete = index < this.currentStageIndex;

                return {
                    label,
                    status: isComplete ? COMPLETE : IN_PROGRESS,
                    disabled: !isComplete,
                };
            });
        },

        emailTemplate() {
            return {
                id: this.email.templateId,

                from: this.email[STEPS.SENDER].from,
                replyToAddress: this.email[STEPS.SENDER].replyToAddress,

                subject: this.email[STEPS.SUBJECT].subject,
                previewText: this.email[STEPS.SUBJECT].previewText,

                toAddress: this.email[STEPS.AUDIENCE].toAddress,

                htmlBody: this.email[STEPS.CONTENT].htmlBody,
                design: this.email[STEPS.CONTENT].design,

                title: this.email.templateTitle,
                trackedLinks: this.trackedLinks,
            };
        },

        hasContent() {
            return validateUnlayerEmail(this.email, STEPS.CONTENT);
        },
    },

    methods: {
        handleCreateSavedTemplate(template) {
            this.savedTemplateToCreate = template;
        },

        handleSavedTemplateCreated() {
            this.savedTemplateToCreate = null;
        },

        goToStage(index) {
            this.currentStageIndex = index;
        },

        handleModalBack() {
            if (this.currentStageIndex === STAGE_INDEXES.TEMPLATE) {
                this.$emit('close');

                return;
            }

            this.goToStage(this.currentStageIndex - 1);
        },

        templateChangeGuideRoutine() {
            if (this.currentStageIndex !== STAGE_INDEXES.CREATE) {
                this.isTemplateChangeTipVisible = false;

                return;
            }

            if (!this.ftuItemsLoaded) {
                return;
            }

            if (this.ftuItems[TEMPLATE_CHANGE_GUIDE]) {
                return;
            }

            if (this.timeoutHandle) {
                return;
            }

            // Display guide after some time as unlayer template
            // takes some time to load and also because displaying
            // guide immediately does not look good in this scenario
            this.timeoutHandle = setTimeout(() => {
                if (this.currentStageIndex === STAGE_INDEXES.CREATE) {
                    // stage index might have changed by this time, so need to confirm
                    this.isTemplateChangeTipVisible = true;
                }

                this.timeoutHandle = null;
            }, TEMPLATE_CHANGE_GUIDE_DISPLAY_DELAY_MS);
        },

        async handleTemplateChangeTipClosed() {
            this.isTemplateChangeTipVisible = false;
            this.ftuItems[TEMPLATE_CHANGE_GUIDE] = true;

            await updateFtuItem(this.coreConfig.appId, this.coreConfig.userId, TEMPLATE_CHANGE_GUIDE, true);
        },

        handleTemplateSelected(template) {
            template.htmlBody = template.html;
            delete template.html;

            this.$emit('template-selected', template);

            this.currentStageIndex = STAGE_INDEXES.CREATE;
        },

        handleTemplateCreated(data) {
            data.htmlBody = data.html;
            delete data.html;

            this.$emit('template-created', data);

            this.currentStageIndex = STAGE_INDEXES.CREATE;
        },

        async fetchMergeFields() {
            // broadcasts and automations use same endpoint to load merge fields.
            // If funnelId is valid, which it should be in automations, then it
            // will load funnel specific merge fields as well.
            this.mergeFields = await getMergeFields(this.funnelId);
        },

        async fetchCampaignLinks() {
            if (this.email.source !== SOURCES.CAMPAIGN) return;

            try {
                const data = await getCampaignLinks(this.funnelId);

                this.campaignLinks = data.links.map((link) => {
                    const idValue = link.linkKey;

                    return {
                        value: idValue,
                        label: link.name,
                    };
                });
            } catch (e) {
                this.$error({ message: this.$t('funnel.unlayerEmail.error.loadingCampaignLinks') });
            }
        },

        async fetchUsers() {
            this.users = await getUsers();
        },

        async fetchUserSignatures() {
            this.userSignatures = await getUserSignatures();
        },

        async fetchAppointmentTypes() {
            this.appointmentTypes = await getAppointmentTypes();
        },

        async fetchTags() {
            const options = this.coreConfig?.isUnlayerExtraTagOverrideEnabled
                ? { pageSize: UNLAYER_EXTRA_TAGS_PAGE_SIZE, pageCount: UNLAYER_EXTRA_TAGS_PAGE_COUNT }
                : {};
            const tags = await getTags(options);

            this.tags = mergeCategoryAndTagName(tags);
        },

        async fetchUnlayerOptions() {
            this.unlayerAppointmentToolEnabled = !this.isMaxClassic; // enabled for pro, max, and ultimate
        },

        async loadFtuItems() {
            this.ftuItems = await loadFtu(this.coreConfig.appId, this.coreConfig.userId);

            this.ftuItemsLoaded = true;
        },

        async fetchBrandColors() {
            if (this.coreConfig.unlayerEmailLogoToolEnabled) {
                try {
                    const { palette } = await getBrandPalette();

                    this.brandColors = palette.map((c) => c.color);
                } catch (e) {
                    sentry.captureException(`Error loading branding colors in Automation. Error: ${e.message}`)
                }
            }
        },

    },
};
</script>

<style lang="scss" scoped>
.header-left {
    display: flex;
}

.header-title {
    display: flex;
    align-items: center;
    margin-right: $spacing-150;
}

.stage-indicator-container {
    position: fixed;
    top: 0;
    height: 1px;
    z-index: 10;
    width: calc(100% - #{$spacing-600});
    text-align: center;
}

.template-change-tip-popover {
    --popover-width: px-to-rem(250);
    left: px-to-rem(125);
    z-index: 2;
}

.stages-container {
    height: calc(100vh - #{px-to-rem(56px)});
    margin: -$spacing-300;
    overflow: hidden;
}

.spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
</style>

<style lang="scss">
div.unlayer-email-broadcast-wizard-modal {
    div.modal-header{
        padding: $spacing-100;
    }

    div.modal-body {
        padding: 0;

        ul.tabs {
            margin-bottom: 0;
            margin-top: 0;

            li.tab {
                padding: 0 0.75rem;
            }
        }
    }
}
</style>
